import { AuthProvider, useAuth } from '@/auth/auth-provider'
import { useLayoutEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { DefaultAuthenticatedRedirect } from './components/DefaultAuthenticatedRedirect'
import { Layout } from './components/Layout/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { RequireSuper } from './components/RequireSuper'
import { DealershipProvider } from './contexts/DealershipContext'
import { apiClient } from './services/api'

// Auth pages (keeping for now, can be removed later)
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { LoginPage } from './pages/auth/LoginPage'
import { UnauthorizedPage } from './pages/auth/UnauthorizedPage'

// Group pages
import { GroupConfiguration } from './pages/group/Configuration/GroupConfiguration'
import { GroupDashboard } from './pages/group/Dashboard/GroupDashboard'
import { AddDealership, DealersList } from './pages/group/Dealers'

// Dealership pages
import { ActivitiesDashboard } from './pages/dealership/Activities/ActivitiesDashboard'
import { AppointmentsDashboard } from './pages/dealership/Appointments/AppointmentsDashboard'
import { DealershipConfiguration } from './pages/dealership/Configuration/DealershipConfiguration'
import { ConversationsList } from './pages/dealership/Conversations/ConversationsList'

// Admin pages
import { AddDealershipGroup } from './pages/admin/AddDealershipGroup'
import { SystemAdministration } from './pages/admin/SystemAdministration'

// Profile page
import { UserProfile } from './pages/profile/UserProfile'

// Registers the API client's token source once someone is signed in.
function ApiInitializer() {
  const { getAccessToken, isAuthenticated } = useAuth()

  // useLayoutEffect so the token getter is registered before child useEffects fire API calls
  useLayoutEffect(() => {
    if (!isAuthenticated) {
      apiClient.clearAuthTokenGetter()
      apiClient.clearOnAuthFailure()
      return
    }

    apiClient.setAuthTokenGetter(getAccessToken)

    // Do not redirect on an API 401 — that creates a loop when the session is valid but
    // the API rejects the request for another reason, such as no role being granted.
    apiClient.clearOnAuthFailure()
  }, [isAuthenticated, getAccessToken])

  return null
}

function App() {
  return (
    <AuthProvider>
      <ApiInitializer />
      <BrowserRouter>
        <DealershipProvider>
          <Routes>
            {/* Public routes (keeping for now, can be removed later) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Default landing: role-aware (see DefaultAuthenticatedRedirect) */}
              <Route index element={<DefaultAuthenticatedRedirect />} />

              {/* Group routes */}
              <Route path="group">
                <Route path="dashboard" element={<GroupDashboard />} />
                <Route path="dealers" element={<DealersList />} />
                <Route path="dealers/new" element={<AddDealership />} />
                <Route path="configuration" element={<GroupConfiguration />} />
              </Route>

              {/* Dealership routes */}
              <Route path="dealership">
                <Route path="conversations" element={<ConversationsList />} />
                <Route path="appointments" element={<AppointmentsDashboard />} />
                <Route path="activities" element={<ActivitiesDashboard />} />
                <Route path="configuration" element={<DealershipConfiguration />} />
              </Route>

              {/* Admin routes — internal tools, super only; matches isSuper on the API */}
              <Route path="admin">
                <Route
                  path="system"
                  element={
                    <RequireSuper>
                      <SystemAdministration />
                    </RequireSuper>
                  }
                />
                <Route
                  path="system/add-group"
                  element={
                    <RequireSuper>
                      <AddDealershipGroup />
                    </RequireSuper>
                  }
                />
              </Route>

              {/* Profile route */}
              <Route path="profile" element={<UserProfile />} />

              {/* Legacy routes (redirect to new structure) */}
              <Route path="dashboard" element={<DefaultAuthenticatedRedirect />} />
              <Route path="dealers" element={<Navigate to="/group/dealers" replace />} />
              <Route path="analytics" element={<Navigate to="/group/analytics" replace />} />
              <Route path="settings" element={<Navigate to="/group/configuration" replace />} />

              {/* Catch all — matrix-aligned default (same as index) */}
              <Route path="*" element={<DefaultAuthenticatedRedirect />} />
            </Route>
          </Routes>
        </DealershipProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}

export default App
